import {
  ActivityMix,
  ActivityType,
} from '@shared/models/design/activities/activity-type.model';
import { sumObjects, sumProperties } from '../math.helpers';
import { filterObject, getKeys } from '../object.helpers';
import { activityRefGuards } from './guards/activity-guards.helpers';
import {
  Building,
  BuildingDesign,
} from '@shared/models/design/building-design.model';
import { Storey } from '@shared/models/design/storey.model';
import {
  calculateStoreyContext,
  getActivityReferenceArea,
} from './activities.helpers';
import { getGfaFromStoreys } from './shape.helpers';
import { getAllStoreys } from './storey.helpers';
import { ActivityExpressionVariablesRecord } from '@shared/models/design/design-expression-variables.model';

/**
 * Get activity areas from every building's storeys and design.activities
 * Calculates area for each activity by summing storey areas multiplied by activity reference shares
 * @param design Building design with activities and storeys
 * @param groupBy 'id' or 'type' to group activities by. Defaults to 'type'.
 * @returns Record mapping activity id to area in m²
 */
export const getActivityMixFromDesign = (design: BuildingDesign): ActivityMix =>
  sumObjects(
    getAllStoreys(design).map((storey) =>
      getActivityMixFromStorey(design, storey),
    ),
  );

/**
 * Activity areas for a single {@link Building} (DEV-2576).
 * @param design Building design that owns {@link building}
 * @param building Building to summarize
 * @returns Record mapping activity type to area in m²
 */
export const getActivityMixFromBuilding = (
  design: BuildingDesign,
  building: Building,
): ActivityMix =>
  sumObjects(
    building.storeys.map((storey) => getActivityMixFromStorey(design, storey)),
  );

/**
 * Get activity areas from storey and storey activities
 * Calculates area for each activity by summing storey area multiplied by activity reference shares
 * @param storey Storey with activities
 * @returns Record mapping activity type to area in m²
 */
export const getActivityMixFromStorey = (
  design: BuildingDesign,
  storey: Storey,
): ActivityMix => {
  const gfa = getGfaFromStoreys(storey);
  if (gfa <= 0) return {};

  // Compute context once so all activity area lookups share the same intermediate values
  const ctx = calculateStoreyContext(design, storey);

  const mix = storey.activities.reduce((acc, activity) => {
    acc[activity.type] =
      (acc[activity.type] ?? 0) +
      getActivityReferenceArea(design, storey, activity, ctx);
    return acc;
  }, {} as ActivityMix);

  const total = sumProperties(mix);
  const remaining = gfa - total;

  if (remaining > 0.1) {
    mix[ActivityType.Unset] = remaining;
  }

  return mix;
};

/**
 * Get the net internal area (NIA) from a design.
 * @param design
 * @returns
 */
export const getNiaFromDesign = (design: BuildingDesign) => {
  const activityMix = getActivityMixFromDesign(design);
  return getProgramActivityAreaFromActivityMix(activityMix);
};

/**
 * Get program activity mix from activity mix
 * @param activityMix Activity mix
 * @param includeUnset Include unset activity in the mix
 * @returns Program activity mix
 */
export const getProgramActivityMixFromActivityMix = (
  activityMix: ActivityMix,
  includeUnset: boolean = false,
): ActivityMix =>
  filterObject(
    activityMix,
    (activity, type) =>
      activityRefGuards.is.programActivityType(type) ||
      (includeUnset && type === ActivityType.Unset),
  );

/**
 * Get program activity area from activity mix
 * @param activityMix Activity mix
 * @param includeUnset Include unset activity in the mix
 * @returns Program activity area
 */
export const getProgramActivityAreaFromActivityMix = (
  activityMix: ActivityMix,
  includeUnset: boolean = false,
): number => {
  return sumProperties(
    getProgramActivityMixFromActivityMix(activityMix, includeUnset),
  );
};

export const omitCalculatedAreaActivitiesFromActivityMix = (
  activityMix: ActivityMix,
): ActivityMix => {
  return filterObject(
    activityMix,
    (activity, type) => !activityRefGuards.is.calculatedAreaActivityType(type),
  );
};

export const getOtherActivityAreaFromActivityMix = (
  activityMix: ActivityMix,
): number => {
  return sumProperties(
    filterObject(activityMix, (activity, type) =>
      activityRefGuards.is.otherActivityType(type),
    ),
  );
};

/**
 * Get common & other activity area from activity mix (excluding unset).
 * @param activityMix Activity mix
 * @returns Common & other activity area
 */
export const getCommonAndOtherAreaFromActivityMix = (
  activityMix: ActivityMix,
): number => {
  return sumProperties(
    filterObject(
      activityMix,
      (activity, type) =>
        activityRefGuards.is.otherActivityType(type) &&
        type !== ActivityType.Unset,
    ),
  );
};

/**
 * Get activity mix factors as factors (shares of 1) for each activity type.
 * @param activityMix Activity mix
 * @returns Activity mix factors, sum should be 1
 */
export const getActivityMixFactors = (
  activityMix: ActivityMix,
): ActivityMix => {
  const total = sumProperties(activityMix) ?? 0;
  return Object.fromEntries(
    Object.entries(activityMix).map(([type, activity]) => [
      type,
      total > 0 ? activity / total : 0,
    ]),
  );
};

/**
 * Get the largest activity type from an activity mix
 * @param activityMix Activity mix
 * @returns Largest activity type, or Unset if all activity types are 0
 */
export const getLargestActivityTypeFromActivityMix = (
  activityMix: ActivityMix,
): ActivityType => {
  return getKeys(activityMix).reduce((a: ActivityType, b: ActivityType) => {
    const valueA = activityMix[a] ?? 0;
    const valueB = activityMix[b] ?? 0;
    return valueA > valueB ? a : b;
  }, ActivityType.Unset);
};

/**
 * Largest {@link ProgramActivityType} in the mix (residential, office, school, garage, …).
 * Non-program types are ignored: calculated areas (facades, innerwalls, stairwells,
 * installations) and Storage, Utilities, Other, and Unset.
 * Returns {@link ActivityType.Unset} when the program mix is empty or all zeros.
 */
export const getLargestProgramActivityTypeFromActivityMix = (
  activityMix: ActivityMix,
): ActivityType =>
  getLargestActivityTypeFromActivityMix(
    getProgramActivityMixFromActivityMix(activityMix),
  );

export const activityExpressionVariablesRecordToActivityMix = (
  activityExpressionVariablesRecord: ActivityExpressionVariablesRecord,
): ActivityMix => {
  return Object.fromEntries(
    Object.entries(activityExpressionVariablesRecord).map(
      ([type, activity]) => [type, activity.gfa ?? 0],
    ),
  );
};
